// src/reports/RefundAnalyticsReport.jsx — Reporting → Store Reports → Backend Reporting → Refund Analytics
// (Eric, 2026-10-18). Replaces the Grafana "Lightspeed Refund Analytics Dashboard". Reads OpsHub's own Lightspeed
// tables (operations_lightspeed_refunds_v) live; needs the Extra Permission reports.backend (finance + admin).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, Spinner, Box, Badge, Tooltip } from '@shopify/polaris';
import { LineChart } from '@shopify/polaris-viz';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { withColors, seriesColor } from './chartPalette';
import ColorDonut from './ColorDonut';
import ReportGuide from './ReportGuide';
import { ReportDateRange, ReportPicker, isoDaysAgo, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtP = n => (n === null || n === undefined) ? '—' : (Number(n) * 100).toFixed(1) + '%';

// kinds: s text, n count, u dollars, p fraction as %, f warning flag
const COL_HELP = {
    n: 'Number of refunds (a refund is one negative payment on a completed sale whose total is negative).',
    amount: 'Dollars refunded.',
    avg: 'Dollars refunded ÷ number of refunds.',
    no_receipt: 'Refunds with no link back to an original sale: no refunded-payment reference and no returned line that points at an earlier sale.',
    no_receipt_pct: 'No-receipt refunds ÷ all refunds.',
    diff_card: 'Card refunds where the card the money went back to is not the card used on the original sale.',
    card_pairs: 'Last four digits: card refunded to ← card on the original sale.',
    methods: 'How the money went back (cash, credit card, gift card…).',
    employees: 'Employees who processed refunds to this card.',
    stores: 'Stores that refunded to this card.',
};

const COLS = {
    detail: [['date', 'Date', 's'], ['time', 'Time', 's'], ['sale_id', 'Sale ID', 's'], ['store', 'Store', 's'], ['employee', 'Employee', 's'],
        ['amount', 'Amount', 'u'], ['refund_method', 'Refund Method', 's'], ['refund_card', 'Refund Card', 's'], ['original_sale', 'Orig Sale', 's'],
        ['original_method', 'Orig Method', 's'], ['original_card', 'Orig Card', 's'], ['customer', 'Customer', 's'], ['no_receipt', 'No Receipt', 'f'], ['diff_card', 'Diff Card', 'f']],
    store: [['store', 'Store', 's'], ['n', 'Refund Count', 'n'], ['amount', 'Refund $', 'h'], ['avg', 'Avg Refund', 'u'], ['no_receipt', 'No Receipt', 'n'],
        ['no_receipt_pct', 'No Receipt %', 'p'], ['diff_card', 'Diff Card', 'n']],
    employee: [['employee', 'Employee', 's'], ['store', 'Store', 's'], ['n', 'Refund Count', 'n'], ['amount', 'Refund $', 'h'], ['avg', 'Avg Refund', 'u'],
        ['no_receipt', 'No Receipt', 'n'], ['diff_card', 'Diff Card', 'n']],
    diff: [['employee', 'Employee', 's'], ['store', 'Store', 's'], ['n', 'Count', 'n'], ['amount', 'Total $', 'u'], ['card_pairs', 'Card Pairs (refund ← orig)', 's']],
    noreceipt: [['employee', 'Employee', 's'], ['store', 'Store', 's'], ['n', 'No Receipt Count', 'n'], ['amount', 'Total $', 'h'], ['methods', 'Refund Methods', 's']],
    cards: [['brand', 'Brand', 's'], ['card', 'Card', 's'], ['n', 'Refund Count', 'n'], ['amount', 'Total $', 'h'], ['avg', 'Avg $', 'u'], ['employees', 'Employees', 's'], ['stores', 'Stores', 's']],
};
const TABS = [
    ['detail', 'Transaction detail'], ['store', 'By store'], ['employee', 'By employee'], ['diff', 'Different card'],
    ['noreceipt', 'No receipt'], ['cards', 'Most-refunded cards'], ['methods', 'Payment methods'],
];

// white (low) → red (high) background for the $ columns, like Grafana's continuous colour
const heat = (v, hi) => {
    const t = Math.max(0, Math.min(1, (Number(v) || 0) / (hi || 1)));
    return { background: `hsl(${Math.round(60 - t * 60)}, ${Math.round(30 + t * 35)}%, ${Math.round(94 - t * 10)}%)`, borderRadius: 4, padding: '2px 6px', display: 'inline-block', minWidth: 70, textAlign: 'right' };
};

function SortTable({ cols, rows, fit = true, limitNote = null }) {
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const hi = useMemo(() => Math.max(1, ...rows.map(r => Number(r.amount) || 0)), [rows]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    const cell = (r, k, kind) => {
        const v = r[k];
        if (kind === 'n') return fmtN(v);
        if (kind === 'u') return fmtUSD(v);
        if (kind === 'h') return <span style={heat(v, hi)}>{fmtUSD(v)}</span>;
        if (kind === 'p') return fmtP(v);
        if (kind === 'f') return v ? <Badge tone="warning">Yes</Badge> : '';
        return v ?? '';
    };
    return (
        <BlockStack gap="200">
            {limitNote && <Text as="p" variant="bodySm" tone="subdued">{limitNote}</Text>}
            <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : 460 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                        <tr>{cols.map(([k, label, kind]) => (
                            <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                                style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                                <Tooltip content={COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>))}</tr>
                    </thead>
                    <tbody>{sorted.map((r, i) => (
                        <tr key={i}>{cols.map(([k, , kind]) => (
                            <td key={k} style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {cell(r, k, kind)}</td>))}</tr>))}
                    </tbody>
                </table>
                {rows.length === 0 && <Box padding="400"><Text as="p" tone="subdued" alignment="center">No refunds match.</Text></Box>}
            </div>
        </BlockStack>
    );
}

function Stat({ label, value, hint, tone }) {
    const body = (
        <Box padding="300" background={tone === 'warn' ? 'bg-surface-warning' : 'bg-surface-secondary'} borderRadius="200">
            <BlockStack gap="050"><Text as="p" variant="bodySm" tone="subdued">{label}</Text><Text as="p" variant="headingLg">{value}</Text></BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

const B = ({ children }) => <b>{children}</b>;
const GUIDE = {
    summary: 'Every refund rung up in a Soccer Post store, by store and employee, with the checks that flag unusual ones: refunds to a different card than the purchase, returns with no receipt, and the same card refunded again and again.',
    sections: [
        { heading: 'What it is for', items: [
            'See how much is being refunded, where, and by whom, over any dates.',
            <>Spot refunds worth a second look: <B>Different card</B> (the money went to a card other than the one used to buy), <B>No receipt</B> (no original sale to tie the refund to) and cards refunded two or more times.</>,
            'See how refunds were paid out (cash, card, gift card, store credit) and how the original purchase was paid.',
        ] },
        { heading: 'Where the numbers come from', items: [
            "OpsHub's own copy of Lightspeed (our point-of-sale system): its sales, payments, employees, stores and customers.",
            'The copy is refreshed every night (the 1:15 AM Lightspeed Sync), so the report is complete through last night and does not include today.',
            "Card brand and last four digits are read from each payment's receipt in Lightspeed.",
            'Dates are the store\'s own local date.',
        ] },
        { heading: 'What counts as a refund', items: [
            'A negative payment on a completed, non-voided sale whose total is negative. Payments Lightspeed has deleted or replaced (archived) are left out.',
            'Putting money onto a gift card is not a refund and is not counted. We checked the count and dollars against Lightspeed directly and they match.',
        ] },
        { heading: 'The boxes and columns', items: [
            <><B>Total refund $ / count / average</B>: sum, number and average size of the refunds in view.</>,
            <><B>Different card</B>: both cards are known and their last four digits differ. <B>Original card</B> is the card on the sale being refunded.</>,
            <><B>No receipt</B>: the refund cannot be tied to an earlier sale (no reference to the payment being refunded, and no returned item points back to an earlier sale).</>,
            <><B>Employee</B>: whoever took the refund. "(archived)" means they have left; "No employee recorded" means Lightspeed itself has none.</>,
            <><B>Methods</B>: debit counts as Credit Card; Credit Account is store credit. On the original payment, "No original sale" means a no-receipt return and "Original sale had no payment" means the earlier sale was $0 or was itself a refund.</>,
        ] },
        { heading: 'How to use it', items: [
            'Pick dates, stores and employees (leave blank for everyone) and press Run Report. The address in your browser holds your choices, so you can share the exact view.',
            'Tabs switch between the transaction log, by-store, by-employee, different-card, no-receipt and most-refunded-card tables, and the payment-method charts.',
            'Click a column title to sort; hover a title for its meaning. Export CSV saves the table you are looking at (the transaction log shows the latest 500).',
            'The chart draws one line per store of refund dollars per day.',
        ] },
    ],
};

export default function RefundAnalyticsReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(sp.get('from') || isoDaysAgo(364));
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [stores, setStores] = useState(sp.getAll('stores'));
    const [emps, setEmps] = useState(sp.getAll('employees'));
    const [tab, setTabState] = useState(Math.max(0, TABS.findIndex(t => t[0] === sp.get('tab'))));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'employees', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo);
            f.stores.forEach(v => n.append('stores', v)); f.emps.forEach(v => n.append('employees', v));
            if (tabIdx > 0) n.set('tab', TABS[tabIdx][0]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, stores, emps }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, stores, emps }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            stores.forEach(v => p.append('stores', v)); emps.forEach(v => p.append('employees', v));
            const r = await fetchAuth(`/api/reports/backend/refund-analytics?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, stores, emps, tab, writeUrl]);
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const storeOptions = useMemo(() => (data?.store_options || []).map(v => ({ value: v, label: v })), [data]);
    const empOptions = useMemo(() => (data?.employee_options || []).map(v => ({ value: v, label: v })), [data]);

    // Daily $ by store: one line per store (every day in the range, so a quiet day is $0, not a gap).
    const lineData = useMemo(() => {
        if (!data?.daily?.length) return [];
        const days = [];
        for (let d = new Date(`${data.date_from}T00:00:00Z`); d <= new Date(`${data.date_to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) days.push(d.toISOString().slice(0, 10));
        const by = {}, tot = {};
        data.daily.forEach(r => { (by[r.store] = by[r.store] || {})[r.date] = r.amount; tot[r.store] = (tot[r.store] || 0) + r.amount; });
        return withColors(Object.keys(by).sort((a, b) => tot[b] - tot[a]).map(st => ({
            name: st, data: days.map(d => ({ key: d, value: Math.round((by[st][d] || 0) * 100) / 100 })),
        })));
    }, [data]);
    const donut = (rows) => (rows || []).map((r, i) => ({ name: r.name, color: seriesColor(i), data: [{ key: 'amount', value: r.amount }] }));

    const s = data?.summary;
    const key = TABS[tab][0];
    const rowsFor = { detail: data?.detail, store: data?.by_store, employee: data?.by_employee, diff: data?.diff_card, noreceipt: data?.no_receipt, cards: data?.top_cards };
    const exportCsv = () => {
        if (!data || !COLS[key]) return;
        downloadCsv(`refund-analytics-${key}-${dateFrom}-${dateTo}.csv`, [COLS[key].map(c => c[1]), ...(rowsFor[key] || []).map(r => COLS[key].map(c => r[c[0]] ?? ''))]);
    };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <ReportGuide id="refund-analytics" summary={GUIDE.summary} sections={GUIDE.sections} />
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                        <ReportPicker label="Store" options={storeOptions} value={stores} onChange={setStores} multiple searchable placeholder="All stores" />
                        <ReportPicker label="Employee" options={empOptions} value={emps} onChange={setEmps} multiple searchable placeholder="All employees" />
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data || !COLS[key]}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        Every refund rung up in a Soccer Post store (Lightspeed), live from OpsHub. A refund is a negative payment on a completed sale whose total is negative;
                        loading a gift card is not a refund. Dates are the store's local date. Different Card compares the last four digits of the card refunded to with the card on the original sale.
                    </Text>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {s && (
                <InlineGrid columns={{ xs: 2, sm: 3, lg: 5 }} gap="200">
                    <Stat label="Total refund $" value={fmtUSD(s.total)} />
                    <Stat label="Total refund count" value={fmtN(s.n)} />
                    <Stat label="⚠️ Different card refunds" value={fmtN(s.diff_card)} tone={s.diff_card ? 'warn' : undefined} hint="Card refunds to a different card than the original sale was paid with." />
                    <Stat label="⚠️ No-receipt returns" value={fmtN(s.no_receipt)} tone={s.no_receipt ? 'warn' : undefined} hint="Refunds with no link back to an original sale." />
                    <Stat label="Avg refund amount" value={fmtUSD(s.avg)} />
                </InlineGrid>
            )}

            {data && lineData.length > 0 && (
                <Card>
                    <BlockStack gap="200">
                        <Text as="h2" variant="headingMd">Daily refund $ by store</Text>
                        <Text as="p" variant="bodySm" tone="subdued">One line per store, highest refund dollars first. Click a store in the legend to isolate it.</Text>
                        <div style={{ height: 380 }}><LineChart data={lineData} yAxisOptions={{ labelFormatter: v => "$" + Number(v).toLocaleString() }} tooltipOptions={{ valueFormatter: fmtUSD }} /></div>
                    </BlockStack>
                </Card>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={TABS.map(([id, content]) => ({ id, content }))} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {key === 'methods' ? (
                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                                <BlockStack gap="200"><Text as="h3" variant="headingSm">Refund payment method $</Text>
                                    <div style={{ height: 300 }}><ColorDonut data={donut(data.refund_methods)} format={fmtUSD} height={300} /></div></BlockStack>
                                <BlockStack gap="200"><Text as="h3" variant="headingSm">Original payment method $</Text>
                                    <div style={{ height: 300 }}><ColorDonut data={donut(data.original_methods)} format={fmtUSD} height={300} /></div></BlockStack>
                            </InlineGrid>
                        ) : (
                            <SortTable key={key} cols={COLS[key]} rows={rowsFor[key] || []}
                                limitNote={key === 'detail' && data.detail_total > data.detail_limit ? `Showing the latest ${fmtN(data.detail_limit)} of ${fmtN(data.detail_total)} refunds — narrow the dates or filters (Export CSV has the same rows).` : null} />
                        )}
                    </Box>
                </Card>
            )}
        </BlockStack>
    );
}
